import { ChangeDetectorRef, Component } from '@angular/core';
import {
	Observable,
	catchError,
	distinctUntilChanged,
	filter,
	map,
	throwError,
	tap,
	switchMap,
	retry,
	pairwise,
	timer,
	debounce,
	Subscription
} from 'rxjs';
import { MechanicsService } from './_services/mechanics.service';
import { SearchService } from './_services/search.service';
import { environment } from "../environments/environment"

import { decrypt, deepClone, compareDictionaries, trimPluses } from './utils';

// https://stackoverflow.com/a/70483901
import { ApexOptions, ApiResponse, Match } from './interfaces';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

const defaultDebounceTime: number = 400;

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    standalone: false
})
export class AppComponent {

	public helpOptions: any[] = [];
	public helpMore: any[] = [];

	public formGroup: UntypedFormGroup = new UntypedFormGroup({

		// Below : keys sent to the API

		term: new UntypedFormControl(''),
		searchType: new UntypedFormControl('stringSearch'),
		inputLang: new UntypedFormControl('en'),
		outputLang: new UntypedFormControl('en'),
		offices: new UntypedFormControl([]),
		display: new UntypedFormControl('all'), // MGS / Nice


		// Below : not sent to the API, just to customize the display

		niceClass: new UntypedFormControl(''), // Exact search : radio choice of which Nice Class is graphed
		sort: new UntypedFormControl('office'),
		showNullResults: new UntypedFormControl(false),
		columnSortBy: new UntypedFormControl('similarityScore'),
		columnSortOrder: new UntypedFormControl(1),
		yearDataDisplay: new UntypedFormControl('projected'), // "projected" || "actual"
		debounceTime: new UntypedFormControl(defaultDebounceTime)
	});

	public apiResponse: Observable<ApiResponse>;
	public errorObj: Error | null = null;
	public alreadyExecutedSearch: boolean = false

	// https://apexcharts.com/docs/angular-charts
	//- @ViewChild("chart") chart: ChartComponent; ? What is this for? (in the doc)
	public chartOptions: Partial<ApexOptions> | any; // Shut up, Typescript!! And compile my goddamn project
	public previousState: any // so as to load it when clicking "back"
	public semanticTermIndex: string = "0"; // When doing a semantic search, this is the index of term we're currently graphing ('sushi' or 'sashimi' etc) to get the corresponding graph
	public environment = environment

	public isEnhancingGraph: string | null = null; // string | null ; office initials if a graph is enhanced, null in 3D mode

	public tableColumns = []; // built in the constructor

	public languageOptions: any[] = [];

	public lastSearchedTerm: string = ""

	public tableCheckboxes: any[] = []

	public isUsingMockData: boolean = false

	constructor(public ms: MechanicsService,
		private router: Router,
		private activatedRoute: ActivatedRoute,
		public ss: SearchService,
		private changeDetector: ChangeDetectorRef) {

		const l = `app.constructor - `
		this.languageOptions = ms.availableLangs.map(lang => ({ "code": lang }))

		this.apiResponse = this.formGroup.valueChanges.pipe(
			/*
			tap((v) => {
				console.log(`${l}TAP - Value changed = `, v)
			}),
			*/
			pairwise(),
			filter(([oldFormData, newFormData]): boolean => {

				const l = `valueChanges filter - `

				// console.log(`\n${l}oldFormData = `, oldFormData)
				// console.log(`${l}newFormData = `, newFormData)

				// console.log(`${l}oldFormData.debounceTime='${oldFormData.debounceTime}', newFormData.debounceTime='${newFormData.debounceTime}', will search? ${oldFormData.debounceTime === 0 && newFormData.debounceTime === defaultDebounceTime}`)

				this.alreadyExecutedSearch = compareDictionaries(oldFormData, newFormData, ["debounceTime"]);
				// console.log(l, oldFormData.term, newFormData.term, this.alreadyExecutedSearch)

				if (oldFormData.debounceTime === 0 && newFormData.debounceTime === defaultDebounceTime) {
					// After searching with the Enter key (which sets the debounce to 0 in order to search immediately), I reset it to its default value. BUT of course, this will trigger an unwanted search, I want it to be silent. So I catch it here and avoid the call
					return false
				}

				newFormData.term = trimPluses(newFormData.term).toLowerCase();

				if (oldFormData.term !== newFormData.term || newFormData.term.length < 2) {
					// Christophe does not want the search to be triggered on type/debounce, only when the user hits Enter
					this.alreadyExecutedSearch = false
					return false
				}

				// I don't want to remake an API call when we only change the column sort order
				if (oldFormData.columnSortBy !== newFormData.columnSortBy
					||
					oldFormData.columnSortOrder !== newFormData.columnSortOrder
				) {
					// console.log(`${l}Only changed one of the columns sort order, I'm not remaking an API call`)
					return false
				}

				// console.log(`${l}newFormData.term = '${newFormData.term}'`)

				return true
			}),
			distinctUntilChanged(),

			tap(() => {
				this.ss.isLoading = true; // Immediately hiding stuff we shouldn't see anymore, while it's loading, without waiting for the debounce
				//this.setQueryParams()
			}),

			debounce(() => timer(this.getFromGroupKey('debounceTime'))), // dynamic debounce time :D
			switchMap(this.switchMapFunc.bind(this)),

			catchError(err => { // Catch HTTP errors here
				this.ss.isLoading = false;
				this.errorObj = err
				console.error(`${l}caught error = `, err)
				return throwError(() => err); // Apparently catchError is just supposed to return this (?)
			})
		) as Observable<ApiResponse>;

		this.helpOptions = [  // Optional, set custom links for Contact and FAQ  (default = WIPO CONTACT)
			{
				"code": "contact", // - code property is mandatory
				"link": `https://www3.wipo.int/contact/${this.ms.lang}/area.jsp?area=branddb` // - link property is optional
			},

		];
		this.helpMore = [
			{
				"name": "User’s guide",
				"code": "guide",
				"link": `${environment.appUrl}/assets/doc.pdf`,
				"targetTab": "_self"
			}
		]

		// if (!this.ms.isLocalHost) {
		this.loadWipoNavbar()
		// }

		document.addEventListener('wipoLanguageChange', async ($event: CustomEvent) => {

			// Dirty hack to wait for TranslationService observable to actually change the language

			const originalTranslation = "" + this.ms.translate("semantic_search.term");
			let antiloop = 0;

			while (antiloop < 100 && this.ms.translate("semantic_search.term") === originalTranslation) {
				await new Promise(r => setTimeout(r, 10))
				antiloop++
			}

			// Now we're sure the language has changed
			this.buildTableColums()
		})


	}

	async ngAfterViewInit() {

		const l = `appcomponent ngAfterViewInit() - `

		/*
		this.formGroup.patchValue({
			term: "sush"
		})
		this.formGroup.patchValue({
			term: "sushi"
		})
		*/

		this.loadStateFromLS();

		this.readQueryParams();

		while (!this.ms.translations) {
			await new Promise(r => setTimeout(r, 50))
		}
		this.buildTableColums()

	}

	ngAfterViewChecked() {
		if (this.ms.isLocalHost) {
			this.changeDetector.detectChanges();
		}
	}

	afterHttpCall() {

		const l = `appComponent afterHttpCall() - `

		this.ss.isLoading = false;

		setTimeout(() => {
			// Hack (ApexCharts apparently doesn't kow how to do hide "0" labels so I'm selecting and hiding them with pure JS)
			for (let a of Array.from(document.querySelectorAll(".apexcharts-data-labels"))) {
				const b: HTMLElement = a as HTMLElement;
				const textContent: string = "" + b.textContent
				// console.log(`elem.textContent = ${textContent}`)
				if (/^0+$/.test(textContent)) {
					// b.style.display = "none"
					a.remove()
				}
			}
		})

		if (this.getFromGroupKey('debounceTime') !== defaultDebounceTime) {
			this.formGroup.patchValue({ debounceTime: defaultDebounceTime }) // REsetting default debounce after searching with Enter key
		}

		// console.log(`${l}unlocking pipes`)
		this.ms.isPipesLocked = false; // allowing a transform pipe to run just once

		this.buildTableColums()
	}

	back() {
		this.formGroup.patchValue(deepClone(this.previousState))
		this.previousState = null
		this.triggerSearch()
	}

	buildTableColums() {


		const l = `appComponent buildTableColums() - `

		this.tableColumns = [
			{
				label: this.ms.translate("semantic_search.term"),
				value: "term"
			},
			{
				label: this.ms.translate("semantic_search.semantic_proximity"),
				value: "similarityScore",
			},
			{
				label: this.ms.translate("semantic_search.nice_class"),
				value: "NiceClass"
			},
			{
				label: this.ms.translate("semantic_search.cumulated_number"),
				value: "nbTotalOccurences"
			},
			{
				label: this.ms.translate("semantic_search.term_type"),
				value: "type"
			}
		];


		// console.log(`${l}this.tableColumns = `, this.tableColumns)
	}

	get canSearch() {
		return this.getFromGroupKey('term').length > 1 && !this.alreadyExecutedSearch;
	}

	get canSearchBrandDB(): boolean {

		if (this.getFromGroupKey('searchType') == 'exactSearch') {
			return this.getFromGroupKey('term').length > 1
		} else {
			return this.tableCheckboxes.length > 0
		}
	}

	get currentYear(): number {
		return new Date().getFullYear()
	}


	doDisplayColumn(): boolean {

		const l = `doDisplayColumn() - `

		// console.log(`${l}this.getFromGroupKey('display') = `,this.getFromGroupKey('display'));

		if (this.getFromGroupKey('display') !== 'all') return false;

		return
	}

	/*
	doDisplayGbdButton() : boolean {
		const l = `doDisplayGbdButton() - `

		this.getFromGroupKey('searchType')==='exactSearch'

		return true
	}
		*/

	get gbdLink(): string {

		const l = `gbdLink() - `


		let asStructure: any = {};

		switch (this.getFromGroupKey('searchType')) {

			case "exactSearch":
				/*
					?sort=score%20desc
					&start=0
					&rows=30
					&asStructure={
						"_id":"3219",
						"boolean":"AND",
						"bricks":[
							{
								"_id":"321a",
								"key":"goodsServices",
								"value":"sushi",
								"strategy":"Terms" --> "Terms" if exact search, "" if not
							}
						]
					}
				*/
				let today = new Date(),
					thisYear = today.getFullYear(),
					thisMonth: number | string = today.getMonth() + 1,
					thisDay: number | string = today.getDate();

				thisMonth = thisMonth < 9 ? "0" + thisMonth : "" + thisMonth;
				thisDay = thisDay < 9 ? "0" + thisDay : "" + thisDay;

				asStructure = {
					"_id": "1",
					"boolean": "AND",
					"bricks": [
						{
							"_id": "2",
							"key": "goodsServices",
							"value": this.getFromGroupKey("term"),
							"strategy": "Terms" // Exact search
						},
						{
							"_id": "3",
							"key": "regDate",
							"strategy": "Min",
							"value": `${thisYear - 10}-${thisMonth}-${thisDay}`
						},
						{
							"_id": "4",
							"key": "status",
							"value": [
								"Registered",
								"Ended"
							]
						}
					]
				};
				break;

			default: // String and Semantic

				/*
					{
						"_id": "bf02",
						"boolean": "OR",
						"bricks": [
							{
								"_id": "bf03",
								"key": "goodsServices",
								"strategy": "Exact",
								"value": "sushi"
							},
							{
								"_id": "bf06",
								"key": "goodsServices",
								"strategy": "Exact",
								"value": "sushi rolling mats"
							},
							{
								"_id": "bf07",
								"key": "goodsServices",
								"strategy": "Exact",
								"value": "sushi restaurant"
							}
						]
					}
				*/

				asStructure = {
					_id: "1",
					boolean: "OR",
					bricks: []
				};

				for (let index in this.tableCheckboxes) {
					asStructure.bricks.push({
						_id: +index + 2,
						key: "goodsServices",
						strategy: "Exact",
						value: this.tableCheckboxes[index].term

					})
				}
		}

		if (this.getFromGroupKey("offices")) {
			asStructure.bricks.push({
				_id: "" + (asStructure.bricks.length + 2),
				key: "office",
				strategy: "any_of",
				value: this.getFromGroupKey("offices").map(o => o.value)
			})
		}

		// console.log(`${l}asStructure = `, asStructure);

		const stringified = JSON.stringify(asStructure);
		const encoded = encodeURIComponent(stringified);

		let link = `https://branddb.wipo.int/${this.ms.lang}/advancedsearch/results?sort=score%20desc&start=0&rows=30&asStructure=${encoded}`;


		return link
	}

	get dataDisplayText(): string {

		const l = `dataDisplayText() - `

		return this.ms.translate("left_column.year_data_display").replace("{y}", "" + this.currentYear)
	}

	getCountriesOccurencesSum(match: Match): number {

		const l = `getCountriesOccurencesSum() - `

		const selectedCountries = (this.getFromGroupKey("offices") || []).map(o => o.value);

		// console.log(`${l}selectedCountries = `, selectedCountries);

		// console.log(`${l}match = `, match);

		if (selectedCountries.length) {
			return (match.nbOccurences || [])
				// @ts-ignore
				.filter((o: any) => selectedCountries.includes(o.office))
				.map((o: any) => o.value)
				.reduce((partialSum: number, a: number) => partialSum + a, 0)
		} else {
			return match.nbTotalOccurences
		}

	}

	getCurrentDisplayedMatch(rawResponse: ApiResponse): Match | null {

		const l = `getCurrentDisplayedMatch`

		const pickedNiceClass = this.getFromGroupKey("niceClass");

		rawResponse.matches = rawResponse.matches || [];

		let match: Match = rawResponse.matches.find(m => m.NiceClass === pickedNiceClass);

		if (!match) { // means we haven't manually picked a niceClass, and it's empty in the formGroup
			match = rawResponse.matches[0];

			if (match) { // Can still be null! (No API result)
				this.formGroup.patchValue({
					niceClass: match.NiceClass
				})
			}
		}

		return match
	}

	getDisplayedResultsSum(rawResponse: ApiResponse): number {

		const l = `getDisplayedResultsSum() - `

		const currentMatch: Match | null = this.getCurrentDisplayedMatch(rawResponse);

		let sum = 0

		// Add null check to prevent TypeError
		if (!currentMatch || !currentMatch.transformedData) {
			return sum;
		}

		if (this.isEnhancingGraph) {

			const enhancedOffice = currentMatch
				.transformedData
				.find(o => o.office === this.isEnhancingGraph as string);
			
			sum = enhancedOffice ? enhancedOffice.total : 0;

		} else {

			sum = currentMatch
				.transformedData
				.map(o => o.total)
				.reduce((partialSum: number, a: number) => partialSum + a, 0)
		}

		// If we chose "Display projected values for the current year", the number on display is greater than the total number of results. It looks quirky. I only return the minimum of those two numbers.

		return sum

	}

	loadStateFromLS(): void {

		const l = `loadStateFromLS() - `

		try {
			let stateString: string = localStorage.getItem("gseState")

			if (!stateString) {
				// console.log(`${l}No state found in the localStorage.`);
				return
			}

			let state = JSON.parse(stateString);

			for (let entry of Object.entries(state)) {
				// console.log(`${l}Patching state : ${entry[0]}=${entry[1]}`);

				if (["niceClass"].includes(entry[0])) { // Did lead to a bug where the LocalStorage contained niceClass:30, but the API response only had niceClass:42, so no result was displayed. This parameter must not be retained, it must be reset each time
					// console.log(`${l}Skipping ${entry[0]}`);
					continue;
				}

				this.formGroup.patchValue({
					[entry[0]]: entry[1]
				})
			}


		} catch (err) {
			console.error(`${l}Caught error : `, err);
		}

	}

	loadWipoNavbar(): void {

		const l = `appcomponent loadWipoNavbar() - `

		const wipoComponentsPath: string = environment.wipoComponentsPath;

		if (!wipoComponentsPath) {
			// console.log(`${l}No wipoComponentsPath, not loading the WIPO navbar`)
			return
		}

		// console.log(`${l}wipoComponentsPath : `, wipoComponentsPath);

		[
			`${wipoComponentsPath}/polyfills/webcomponents-loader.js`,
			`${wipoComponentsPath}/wipo-init/wipo-init.js`,
			`${wipoComponentsPath}/wipo-navbar/wipo-navbar.js`
		].forEach(src => {
			document.head.appendChild(Object.assign(document.createElement('script'), {
				src,
				async: true
			}));
		});
	};

	getFromGroupKey(key: string) {
		const l = `getFromGroupKey - `
		let formControl = this.formGroup.get(key)
		return formControl?.value || ""
	}

	getTermType(rawResponse: ApiResponse): string {

		const l = `getTermType() - `

		const getCurrentDisplayedMatch: Match | null = this.getCurrentDisplayedMatch(rawResponse);

		console.log(`\n${l}getCurrentDisplayedMatch = `, getCurrentDisplayedMatch);

		if (!getCurrentDisplayedMatch) return "";

		const type: number = getCurrentDisplayedMatch.type;

		console.log(`${l}typesMappingExactSearch = `, structuredClone(this.ms.typesMappingExactSearch));

		console.log(`${l}type = ${type}`);

		const termType = this.ms.typesMappingExactSearch[type];
		return termType || "";
	}

	isRadioChecked(match: Match, index: number): boolean {
		const l = `isRadioChecked - `
		const toReturn: boolean = (this.getFromGroupKey("niceClass") === match.NiceClass) || (index == 0 && !this.getFromGroupKey("niceClass"))
		// console.log(`${l}getFromGroupKey("niceClass")='${this.getFromGroupKey("niceClass")}'; match.NiceClass='${match.NiceClass}'; index='${index}'; returning ${toReturn}`)
		return toReturn
	}

	onRadioClicked(match: Match): void {
		const l = `onRadioClicked() - `
		this.formGroup.patchValue({ niceClass: match.NiceClass })
	}

	enhanceGraph(office?: string) {
		const l = `enhanceGraph() - `
		// console.log(`${l}Enhancing graph '${office}'`)
		this.isEnhancingGraph = (this.isEnhancingGraph === null ? office : null)
	}

	isCheckboxChecked(match: Match): boolean {
		const l = `isCheckboxChecked() - `

		return !!this.tableCheckboxes.find(o => o.term === match.term);
	}

	onCheckboxClicked($event, match: Match) {

		const l = `onCheckboxClicked() - `

		if ($event.target.checked) {
			this.tableCheckboxes.push({ term: match.term })
		} else {
			this.tableCheckboxes = this.tableCheckboxes.filter(o => o.term !== match.term)
		}
	}

	performExactSearch(match: Match) {

		const l = `performExactSearch() - `
		this.previousState = deepClone(this.formGroup.value)

		// console.log(`${l} this.getFromGroupKey('searchType') = `, this.getFromGroupKey('searchType'))

		// Only in semantic search : when the input is "car" (English) and the outputLang is Spanigh, we get results like "carros". If we perform a semantic search, then "carros" is in Spanish, so the inputLang has to change to Spanish, otherwise we won't get any result
		const inputLang = this.getFromGroupKey('searchType') === "semanticSearch" ? this.getFromGroupKey("outputLang") : this.getFromGroupKey("inputLang");

		this.formGroup.patchValue({
			term: match.term_for_link,
			inputLang,
			searchType: "exactSearch",
			niceClass: match.NiceClass
		})
		this.triggerSearch()
	}

	readQueryParams(): void {

		const l = `readQueryParams() - `

		const routeSub: Subscription = this.activatedRoute.queryParams
			.subscribe(params => {

				if (Object.keys(params).length === 0) {
					return
				}

				routeSub.unsubscribe();

				let paramsCopy = structuredClone(params)
				//console.log(`${l}raw params = `, structuredClone(params));

				for (let key of Object.keys(paramsCopy)) {
					if (key == 'offices') {
						if (!Array.isArray(paramsCopy[key])) {
							paramsCopy[key] = [paramsCopy[key]]
						}
						for (let el in paramsCopy[key]) {
							paramsCopy[key][el] = {
								value: paramsCopy[key][el].toUpperCase(),
								label: paramsCopy[key][el].toUpperCase()
							}
						}

					}
					if (paramsCopy[key] === "true") paramsCopy[key] = true;
					if (paramsCopy[key] === "false") paramsCopy[key] = false;
				}

				console.log(`${l}corrected params = `, paramsCopy);

				this.formGroup.patchValue(paramsCopy) // BAM! But... for some reason, does not trigger the search?? Despite .tap() being logged. I guess, because there's an original debounceTime of 400ms, and this happens before, in ngOnInit()
				this.triggerSearch() // I have to do that... Why?
			});

	}

	noMatchesFoundText(): string {
		return this.ms.translate("exact_search.no_matches_found")
			.replace("{S}", this.lastSearchedTerm)

	}

	saveStateToLs(formData: any): void {
		const l = `saveStateToLs() - `
		// console.log(`${l}formData = `, formData);
		localStorage.setItem("gseState", JSON.stringify(formData))
	}

	setQueryParams(): void {

		const l = `setQueryParams() - `

		let queryParams = {};
		const formValue = this.formGroup.value;

		for (const key in formValue) {
			console.log(`${l}appending ${key}=`, formValue[key]);
			queryParams[key] = formValue[key];
		}

		// console.log(`${l}navigating with queryParams = `, queryParams);

		this.router.navigate(
			[],
			{
				relativeTo: this.activatedRoute,
				queryParams,
				queryParamsHandling: 'merge',
			}
		);
	}

	sortBy(what: string) {

		const l = `sortBy() - `

		// console.log(`${l}${what}`)

		this.ms.isPipesLocked = false; // I need to run the sort pipe anyway, even if no HTTP call is made

		const currentSortField = this.getFromGroupKey("columnSortBy");

		// console.log(`${l}currentSortField = ${currentSortField}`) // currentSortField = similarityScore

		if (currentSortField === what) { // just reversing the order
			const currentSortOrder = this.getFromGroupKey("columnSortOrder")

			// console.log(`${l}currentSortOrder = ${currentSortOrder} - Inverting sort order`)

			this.formGroup.patchValue({
				columnSortOrder: currentSortOrder * -1
			})

		} else {

			// console.log(`${l}Declaring columnSortBy="${what}"`);

			this.formGroup.patchValue({
				columnSortBy: what
			})
		}
	}

	switchMapFunc([oldFormData, newFormData]): Observable<any> {

		const l = `app.switchMapFunc() - `

		// console.log(`\n${l}oldFormData = `, oldFormData)
		// console.log(`${l}newFormData = `, newFormData)

		const formData = {
			searchType: newFormData.searchType,
			term: newFormData.term,
			inputLang: newFormData.inputLang,
			outputLang: newFormData.searchType === "semanticSearch" && newFormData.outputLang,
			display: newFormData.display,
			offices: newFormData.offices
		}

		// console.log(`${l}formData = `, formData)

		for (let key of Object.keys(formData)) {
			try {
				formData[key] = formData[key].trim()
			} catch (err) {
				// not a string, that's fine (boolean or array)
			}
		}

		this.saveStateToLs(newFormData);

		return this.ss.callSearch(formData).pipe(
			retry(3),
			map(data => {
				let decrypted: any = decrypt(data);
				if (this.ms.isLocalHost || this.ms.isAwsDev) {
					console.info(`(DEV LOG ONLY)${l} response = `, decrypted)
				}
				// Check if we're using mock data
				this.isUsingMockData = decrypted?.isMock === true;
				decrypted['original_term'] = decodeURI(formData.term);
				this.lastSearchedTerm = "" + decrypted['original_term'];
				this.lastSearchedTerm = this.lastSearchedTerm.split('%2C').join(',')
				return decrypted
			}),
			catchError(err => { // Catch HTTP errors here
				this.ss.isLoading = false;
				this.errorObj = err
				return throwError(() => err); // Apparently catchError is just supposed to return this (?)
			}),
			tap(this.afterHttpCall.bind(this))
		)

	}

	toggleAllCheckboxes($event, transformedResponse) {

		const l = `toggleAllCheckboxes() - `

		// When toggling all checkboxes in a table

		$event.preventDefault();

		const areAllChecked: boolean = this.tableCheckboxes.length === transformedResponse.matches.length;
		this.tableCheckboxes = [];

		console.log(`${l}areAllChecked : `, areAllChecked);

		if (!areAllChecked) {
			this.tableCheckboxes = transformedResponse.matches.map(o => ({ term: o.term }))
		}
	}

	typeString2Word(typeString: string): string {
		const l = `typeString() - `
		return {
			"all" : "-",
			"mgs_us" : "MGS, US",
		}[typeString] || typeString?.toUpperCase() || ""
	}

	isAllCheckboxesChecked(transformedResponse): boolean {
		return this.tableCheckboxes.length === transformedResponse.matches.length
	}

	triggerSearch() {
		const l = `triggerSearch() - `

		/*
			:| Tricky.
			The template is subscribed to form changes.
			But "Enter" is not a form change.
			So I use this hack : on Enter key, a hidden field in the form is updated : debounceTime.
			The Observable is triggered AND we can use the debounceTime=0 value to search immediately!
		*/

		this.alreadyExecutedSearch = true;
		this.formGroup.patchValue({ debounceTime: 0 })
	}

	get windowWidth(): number {
		return window.innerWidth
	}
}
